import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  fr: {
    // Header
    "nav.home": "Accueil",
    "nav.method": "Méthode",
    "nav.services": "Services",
    "nav.cases": "Cas",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.contact": "Contact",
    "cta.audit": "Essai gratuit",
    
    // Hero
    "hero.title": "Transformez vos clients en ambassadeurs fidèles",
    "hero.subtitle": "Créez une expérience client inoubliable en quelques secondes. QR code, avis Google, Instagram — tout en un seul scan. Fidélisez et développez votre communauté locale.",
    "hero.cta.primary": "Commencer gratuitement",
    "hero.cta.secondary": "Découvrir notre méthode",
    "hero.trust": "Ils nous font confiance",
    
    // Stats
    "stats.qualified": "+45% d'avis Google en 30 jours",
    "stats.sectors": "12 secteurs accompagnés",
    "stats.roi": "5 min pour être opérationnel",
    "stats.rgpd": "100% conformité RGPD",
    
    // Approach
    "approach.title": "Comment ça marche",
    "approach.subtitle": "Notre approche simple en 4 étapes pour transformer chaque visite en opportunité de fidélisation.",
    "approach.step1.title": "Scan du QR code",
    "approach.step1.desc": "Le client scanne le QR code sur place.",
    "approach.step2.title": "Choix de l'action",
    "approach.step2.desc": "Instagram, avis Google ou autre action.",
    "approach.step3.title": "Engagement instantané",
    "approach.step3.desc": "Le client agit directement sur la plateforme.",
    "approach.step4.title": "Suivi & analyse",
    "approach.step4.desc": "Visualisez les résultats en temps réel.",
    "approach.cta": "Démarrer maintenant",
    
    // Services
    "services.title": "Nos services",
    "services.subtitle": "Une gamme complète de solutions pour propulser l'engagement de vos clients locaux.",
    "services.mapping.title": "QR Code personnalisé",
    "services.mapping.desc": "Créez un QR code aux couleurs de votre marque pour engager vos clients.",
    "services.optimization.title": "Page d'engagement",
    "services.optimization.desc": "Une page personnalisée qui redirige vers Instagram, Google, ou vos réseaux.",
    "services.tracking.title": "Tableau de bord",
    "services.tracking.desc": "Suivez les scans, clics et engagements en temps réel.",
    "services.training.title": "Support & accompagnement",
    "services.training.desc": "Une équipe à votre écoute pour optimiser vos résultats.",
    
    // Case Studies
    "cases.title": "Des résultats concrets",
    "cases.subtitle": "Découvrez comment nos clients transforment leurs visiteurs en ambassadeurs fidèles.",
    "cases.case1.context": "Contexte",
    "cases.case1.context.text": "Restaurant avec peu d'avis Google et faible présence Instagram.",
    "cases.case1.action": "Action",
    "cases.case1.action.text": "Mise en place de QR codes + page d'engagement.",
    "cases.case1.result": "Résultat",
    "cases.case1.result.text": "+85 avis Google et +200 abonnés Instagram en 2 mois.",
    "cases.case2.context.text": "Boutique locale cherchant à fidéliser sa clientèle.",
    "cases.case2.action.text": "QR code + redirection Instagram et Google.",
    "cases.case2.result.text": "+120% d'engagement client.",
    "cases.cta": "Voir plus de cas",
    
    // Pricing
    "pricing.title": "Nos tarifs",
    "pricing.subtitle": "Des formules adaptées à tous les commerces",
    "pricing.cta": "Choisir",
    
    // FAQ
    "faq.title": "Questions fréquentes",
    "faq.subtitle": "Tout ce que vous devez savoir sur Localove et notre approche.",
    "faq.q1": "Comment fonctionne le QR code ?",
    "faq.a1": "Le client scanne le QR code et accède à une page personnalisée qui le redirige vers votre Instagram ou votre fiche Google pour laisser un avis.",
    "faq.q2": "Combien de temps pour être opérationnel ?",
    "faq.a2": "Moins de 5 minutes ! Créez votre commerce, personnalisez votre page et téléchargez votre QR code.",
    "faq.q3": "Quels types de commerces peuvent utiliser Localove ?",
    "faq.a3": "Restaurants, boutiques, salons de coiffure, instituts, cafés... tout commerce local avec une clientèle physique.",
    "faq.q4": "Puis-je suivre les résultats ?",
    "faq.a4": "Oui, votre tableau de bord vous montre le nombre de scans, clics et engagements en temps réel.",
    "faq.q5": "Puis-je personnaliser ma page ?",
    "faq.a5": "Absolument ! Ajoutez votre logo, vos couleurs et un message personnalisé.",
    "faq.q6": "Y a-t-il un engagement ?",
    "faq.a6": "Non, vous pouvez annuler à tout moment sans frais.",
    
    // AI Visibility (repurposed for local engagement)
    "visibility.badge": "Engagement client nouvelle génération",
    "visibility.title": "Vos clients passent, mais reviennent-ils ?",
    "visibility.title.highlight": "Fidélisez-les.",
    "visibility.desc": "Chaque jour, vos clients découvrent de nouveaux commerces. La question est : comment les incitez-vous à revenir et à vous recommander ? Localove transforme chaque visite en opportunité de fidélisation et de recommandation.",
    "visibility.cited.title": "Avis collectés",
    "visibility.cited.desc": "Suivi en temps réel",
    "visibility.trends.title": "Tendances",
    "visibility.trends.desc": "Analyse de l'engagement",
    "visibility.competition.title": "Performance",
    "visibility.competition.desc": "Comparaison locale",
    "visibility.chart.title": "Évolution des avis",
    "visibility.chart.subtitle": "Suivez la croissance de vos avis Google",
    "visibility.top.title": "Plateformes actives",
    "visibility.top.citations": "clics",

    // AI Metrics (repurposed for local engagement metrics)
    "metrics.title": "Des métriques d'engagement conçues pour les commerces locaux",
    "metrics.cta": "Demander une démo",
    "metrics.brand.title": "Nombre de scans",
    "metrics.brand.desc": "Suivez combien de clients scannent votre QR code.",
    "metrics.competitor.title": "Taux de conversion",
    "metrics.competitor.desc": "Mesurez le pourcentage de scans qui mènent à une action.",
    "metrics.competitor.ranking": "Classement local",
    "metrics.competitor.subtitle": "Commerces les plus engagés",
    "metrics.competitor.brand": "Commerce",
    "metrics.competitor.visibility": "Engagement",
    "metrics.sentiment.title": "Score de satisfaction",
    "metrics.sentiment.desc": "Analysez le sentiment des avis laissés par vos clients.",
    "metrics.sentiment.positive": "Positif",
    "metrics.sentiment.neutral": "Neutre",
    "metrics.sentiment.negative": "Négatif",
    "metrics.ranking.title": "Position locale",
    "metrics.ranking.desc": "Voyez comment vous vous positionnez par rapport aux commerces similaires.",
    "metrics.prompt.title": "Performance par plateforme",
    "metrics.prompt.desc": "Identifiez quelles plateformes génèrent le plus d'engagement.",
    "metrics.prompt.mentions": "clics",
    "metrics.prompt.avg": "moy",
    
    // Contact
    "contact.title": "Démarrez votre essai gratuit",
    "contact.subtitle": "Remplissez ce formulaire et lancez votre QR code d'engagement en quelques minutes.",
    "contact.name": "Nom",
    "contact.firstname": "Prénom",
    "contact.email": "Email",
    "contact.phone": "Téléphone",
    "contact.company": "Nom du commerce",
    "contact.jobTitle": "Fonction",
    "contact.message": "Message",
    "contact.message.placeholder": "Décrivez votre commerce et vos objectifs...",
    "contact.rgpd": "J'accepte que Localove me contacte au sujet de ma demande.",
    "contact.submit": "Démarrer gratuitement",
    
    // CTA Section
    "cta.title": "Transformez vos clients en ambassadeurs",
    "cta.subtitle": "Rejoignez les commerces qui fidélisent leurs clients et transforment chaque visite en recommandation.",
    "cta.button": "Démarrer maintenant",
    
    // Footer
    "footer.legal": "Mentions légales",
    "footer.privacy": "Politique de confidentialité",
    "footer.copyright": "Copyright Localove 2025",
    "footer.back": "↑ Retour en haut",
    
    // Blog
    "blog.title": "Blog Localove",
    "blog.subtitle": "Conseils, guides et astuces pour fidéliser vos clients locaux",
    "blog.readmore": "Lire la suite",
    "blog.back": "← Retour au blog",
  },
  en: {
    // Header
    "nav.home": "Home",
    "nav.method": "Method",
    "nav.services": "Services",
    "nav.cases": "Cases",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.contact": "Contact",
    "cta.audit": "Free trial",
    
    // Hero
    "hero.title": "Turn your customers into loyal ambassadors",
    "hero.subtitle": "Create an unforgettable customer experience in seconds. QR code, Google reviews, Instagram — all in one scan. Build loyalty and grow your local community.",
    "hero.cta.primary": "Start for free",
    "hero.cta.secondary": "Discover our method",
    "hero.trust": "They trust us",
    
    // Stats
    "stats.qualified": "+45% Google reviews in 30 days",
    "stats.sectors": "12 sectors supported",
    "stats.roi": "5 min to get started",
    "stats.rgpd": "100% GDPR compliance",
    
    // Approach
    "approach.title": "How it works",
    "approach.subtitle": "Our simple 4-step approach to turn every visit into a loyalty opportunity.",
    "approach.step1.title": "QR code scan",
    "approach.step1.desc": "Customer scans the QR code on-site.",
    "approach.step2.title": "Choose action",
    "approach.step2.desc": "Instagram, Google review, or other action.",
    "approach.step3.title": "Instant engagement",
    "approach.step3.desc": "Customer acts directly on the platform.",
    "approach.step4.title": "Track & analyze",
    "approach.step4.desc": "View results in real-time.",
    "approach.cta": "Get started now",
    
    // Services
    "services.title": "Our services",
    "services.subtitle": "A complete range of solutions to boost your local customer engagement.",
    "services.mapping.title": "Custom QR Code",
    "services.mapping.desc": "Create a branded QR code to engage your customers.",
    "services.optimization.title": "Engagement page",
    "services.optimization.desc": "A personalized page that redirects to Instagram, Google, or your networks.",
    "services.tracking.title": "Dashboard",
    "services.tracking.desc": "Track scans, clicks, and engagements in real-time.",
    "services.training.title": "Support & guidance",
    "services.training.desc": "A team ready to help optimize your results.",
    
    // Case Studies
    "cases.title": "Real results",
    "cases.subtitle": "Discover how our clients turn visitors into loyal ambassadors.",
    "cases.case1.context": "Context",
    "cases.case1.context.text": "Restaurant with few Google reviews and low Instagram presence.",
    "cases.case1.action": "Action",
    "cases.case1.action.text": "QR codes + engagement page setup.",
    "cases.case1.result": "Result",
    "cases.case1.result.text": "+85 Google reviews and +200 Instagram followers in 2 months.",
    "cases.case2.context.text": "Local boutique looking to build customer loyalty.",
    "cases.case2.action.text": "QR code + Instagram and Google redirect.",
    "cases.case2.result.text": "+120% customer engagement.",
    "cases.cta": "See more cases",
    
    // Pricing
    "pricing.title": "Our pricing",
    "pricing.subtitle": "Plans tailored to all businesses",
    "pricing.cta": "Choose",
    
    // FAQ
    "faq.title": "Frequently asked questions",
    "faq.subtitle": "Everything you need to know about Localove and our approach.",
    "faq.q1": "How does the QR code work?",
    "faq.a1": "The customer scans the QR code and accesses a personalized page that redirects to your Instagram or Google listing to leave a review.",
    "faq.q2": "How long to get started?",
    "faq.a2": "Less than 5 minutes! Create your business, customize your page, and download your QR code.",
    "faq.q3": "What types of businesses can use Localove?",
    "faq.a3": "Restaurants, boutiques, hair salons, spas, cafes... any local business with walk-in customers.",
    "faq.q4": "Can I track results?",
    "faq.a4": "Yes, your dashboard shows scans, clicks, and engagements in real-time.",
    "faq.q5": "Can I customize my page?",
    "faq.a5": "Absolutely! Add your logo, colors, and a personalized message.",
    "faq.q6": "Is there a commitment?",
    "faq.a6": "No, you can cancel anytime without fees.",
    
    // AI Visibility (repurposed for local engagement)
    "visibility.badge": "Next-generation customer engagement",
    "visibility.title": "Your customers visit, but do they return?",
    "visibility.title.highlight": "Build loyalty.",
    "visibility.desc": "Every day, your customers discover new businesses. The question is: how do you encourage them to come back and recommend you? Localove transforms every visit into an opportunity for loyalty and recommendation.",
    "visibility.cited.title": "Reviews collected",
    "visibility.cited.desc": "Real-time tracking",
    "visibility.trends.title": "Trends",
    "visibility.trends.desc": "Engagement analysis",
    "visibility.competition.title": "Performance",
    "visibility.competition.desc": "Local comparison",
    "visibility.chart.title": "Review growth",
    "visibility.chart.subtitle": "Track your Google reviews growth",
    "visibility.top.title": "Active platforms",
    "visibility.top.citations": "clicks",

    // AI Metrics (repurposed for local engagement metrics)
    "metrics.title": "Engagement metrics designed for local businesses",
    "metrics.cta": "Get a demo",
    "metrics.brand.title": "Scan count",
    "metrics.brand.desc": "Track how many customers scan your QR code.",
    "metrics.competitor.title": "Conversion rate",
    "metrics.competitor.desc": "Measure the percentage of scans that lead to action.",
    "metrics.competitor.ranking": "Local ranking",
    "metrics.competitor.subtitle": "Most engaged businesses",
    "metrics.competitor.brand": "Business",
    "metrics.competitor.visibility": "Engagement",
    "metrics.sentiment.title": "Satisfaction score",
    "metrics.sentiment.desc": "Analyze the sentiment of reviews left by your customers.",
    "metrics.sentiment.positive": "Positive",
    "metrics.sentiment.neutral": "Neutral",
    "metrics.sentiment.negative": "Negative",
    "metrics.ranking.title": "Local position",
    "metrics.ranking.desc": "See how you rank compared to similar businesses.",
    "metrics.prompt.title": "Platform performance",
    "metrics.prompt.desc": "Identify which platforms generate the most engagement.",
    "metrics.prompt.mentions": "clicks",
    "metrics.prompt.avg": "avg",
    
    // Contact
    "contact.title": "Start your free trial",
    "contact.subtitle": "Fill out this form and launch your engagement QR code in minutes.",
    "contact.name": "Last name",
    "contact.firstname": "First name",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "contact.company": "Business name",
    "contact.jobTitle": "Job title",
    "contact.message": "Message",
    "contact.message.placeholder": "Describe your business and your goals...",
    "contact.rgpd": "I agree that Localove may contact me regarding my request.",
    "contact.submit": "Start for free",
    
    // CTA Section
    "cta.title": "Turn your customers into ambassadors",
    "cta.subtitle": "Join businesses that build customer loyalty and turn every visit into a recommendation.",
    "cta.button": "Get started now",
    
    // Footer
    "footer.legal": "Legal notice",
    "footer.privacy": "Privacy policy",
    "footer.copyright": "Copyright Localove 2025",
    "footer.back": "↑ Back to top",
    
    // Blog
    "blog.title": "Localove Blog",
    "blog.subtitle": "Tips, guides and tricks to build customer loyalty",
    "blog.readmore": "Read more",
    "blog.back": "← Back to blog",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language || navigator.languages?.[0] || "fr";
  // Check if the browser language starts with 'en'
  if (browserLang.toLowerCase().startsWith("en")) {
    return "en";
  }
  // Default to French for all other languages
  return "fr";
};

const getStoredLanguage = (): Language | null => {
  try {
    const stored = localStorage.getItem("preferred-language");
    if (stored === "fr" || stored === "en") {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return null;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // First check if user has a stored preference
    const stored = getStoredLanguage();
    if (stored) return stored;
    // Otherwise detect from browser
    return getBrowserLanguage();
  });

  // Save language preference when it changes
  useEffect(() => {
    try {
      localStorage.setItem("preferred-language", language);
    } catch {
      // localStorage not available
    }
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.fr] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
